import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "../lib/siteData";

const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/investors", label: "Investors & Developers" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white border-b ${
        scrolled ? "border-slate-200 shadow-sm" : "border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[120px] lg:h-[152px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0 group" data-testid="navbar-logo">
            <img
              src={BRAND.logo}
              alt="LNZ Designs & Development Co. Ltd"
              className="h-24 lg:h-[132px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `relative text-[13px] xl:text-sm font-semibold tracking-wide transition-colors py-2 ${
                    isActive ? "text-[#123E73]" : "text-[#0A192F] hover:text-[#123E73]"
                  }`
                }
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute left-0 right-0 -bottom-0.5 h-0.5 bg-[#F5A400] transition-transform origin-left ${
                        isActive ? "scale-x-100" : "scale-x-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${BRAND.phone}`}
              className="hidden xl:flex items-center gap-2 text-sm font-semibold text-[#0A192F] hover:text-[#123E73] transition-colors"
              data-testid="navbar-phone"
            >
              <Phone className="w-4 h-4" />
              {BRAND.phoneDisplay}
            </a>
            <Link
              to="/contact"
              className="bg-[#F5A400] text-[#0A192F] hover:bg-[#D99000] font-bold px-5 py-3 text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5"
              data-testid="navbar-cta-start-project"
            >
              Start Your Project
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 text-[#0A192F]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="navbar-mobile-menu">
          <div className="flex flex-col p-6 gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-[#0A192F] font-semibold text-base py-3 border-b border-slate-100 ${
                    isActive ? "text-[#123E73]" : ""
                  }`
                }
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="bg-[#F5A400] text-[#0A192F] font-bold px-5 py-3.5 text-sm text-center mt-4"
              data-testid="mobile-navbar-cta"
            >
              Start Your Project
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
